/**
 * Package Info Component
 * Displays current user's package information with expiration date
 * @component PackageInfo
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  AccessTime,
  Warning,
  CheckCircle,
  BusinessCenter,
  Timer
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getUserPackageUsage, PackageUsage } from '../../services/packageService';
import './style.css';

interface PackageInfoProps {
  compact?: boolean;
  showIcon?: boolean;
}

const PackageInfo: React.FC<PackageInfoProps> = ({
  compact = false,
  showIcon = true
}) => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [packageUsage, setPackageUsage] = useState<PackageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPackageInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUserPackageUsage();
        setPackageUsage(data);
      } catch (err) {
        console.error('Failed to fetch package info:', err);
        setError('Не вдалося завантажити інформацію про пакет');
      } finally {
        setLoading(false);
      }
    };

    fetchPackageInfo();
  }, []);

  const formatExpirationDate = (dateString?: string) => {
    if (!dateString) return null;

    const date = new Date(dateString);

    if (language === 'ua') {
      return date.toLocaleDateString('uk-UA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }

    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getExpirationStatus = () => {
    if (!packageUsage?.expiresAt) return null;

    const date = new Date(packageUsage.expiresAt);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (packageUsage.isExpired) {
      return { status: 'expired', days: 0 };
    }

    if (diffDays <= 7) {
      return { status: 'warning', days: diffDays };
    }

    return { status: 'active', days: diffDays };
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'expired':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'active':
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getStatusIcon = (status: string | null) => {
    const iconSize = compact ? 10 : 16;
    switch (status) {
      case 'expired':
        return <Warning sx={{ fontSize: iconSize }} />;
      case 'warning':
        return <AccessTime sx={{ fontSize: iconSize }} />;
      case 'active':
        return <CheckCircle sx={{ fontSize: iconSize }} />;
      default:
        return <BusinessCenter sx={{ fontSize: iconSize }} />;
    }
  };

  const getTargetAudienceLabel = (targetAudience: string) => {
    if (language === 'ua') {
      switch (targetAudience) {
        case 'clients':
          return 'Для клієнтів';
        case 'masters':
          return 'Для майстрів';
        case 'owners':
          return 'Для власників';
        default:
          return targetAudience;
      }
    } else {
      switch (targetAudience) {
        case 'clients':
          return 'For clients';
        case 'masters':
          return 'For masters';
        case 'owners':
          return 'For owners';
        default:
          return targetAudience;
      }
    }
  };

  const expirationStatus = getExpirationStatus();
  const statusColor = getStatusColor(expirationStatus?.status || null);

  if (loading) {
    return (
      <Box className={`package-info ${compact ? 'compact' : ''}`}>
        <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={`package-info ${compact ? 'compact' : ''}`}>
        <Alert severity="error" variant="outlined" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!packageUsage) {
    return null;
  }

  const packageName = packageUsage.package?.name ||
    (language === 'ua' ? 'Безкоштовний' : 'Free');

  return (
    <Box className={`package-info ${compact ? 'compact' : ''}`}>
      <Tooltip
        title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {packageName}
            </Typography>
            {packageUsage.package?.target_audience && (
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, mb: 0.5 }}>
                {getTargetAudienceLabel(packageUsage.package.target_audience)}
              </Typography>
            )}
            {user?.role !== 'client' && (
              <Typography variant="body2">
                {language === 'ua' ? 'Бізнесів використано:' : 'Businesses used:'} {packageUsage.currentUsage}/{packageUsage.maxAllowed === 0 ? '∞' : packageUsage.maxAllowed}
              </Typography>
            )}
            {packageUsage.expiresAt && (
              <Typography variant="body2">
                {language === 'ua' ? 'Діє до:' : 'Expires:'} {formatExpirationDate(packageUsage.expiresAt)}
              </Typography>
            )}
            {expirationStatus?.status === 'expired' && (
              <Typography variant="body2" sx={{ color: theme.palette.error.main, mt: 1 }}>
                {language === 'ua' ? 'Термін дії закінчився!' : 'Package expired!'}
              </Typography>
            )}
          </Box>
        }
        arrow
        placement="bottom"
      >
        <Chip
          icon={showIcon ? getStatusIcon(expirationStatus?.status || null) : undefined}
          label={
            compact ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                  {packageName}
                </Typography>
                {packageUsage.expiresAt && (
                  <Timer sx={{ fontSize: 8, color: statusColor }} />
                )}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {packageName}
              </Typography>
            )
          }
          size={compact ? "small" : "medium"}
          variant="outlined"
          sx={{
            borderColor: statusColor,
            color: statusColor,
            '& .MuiChip-icon': {
              color: statusColor,
            },
            '&:hover': {
              backgroundColor: `${statusColor}10`,
              borderColor: statusColor,
            },
            cursor: 'pointer',
            height: compact ? 18 : 20,
          }}
        />
      </Tooltip>
    </Box>
  );
};

export default PackageInfo;
